import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { useLocation } from 'react-router-dom';
import { BringzzPageContainer, BringzzIcon, BringzzText, BringzzDropdown } from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { alphabets } from './constants';

const Products = () => {
    const { t } = useTranslation();
    const { backPress, navigate } = useNavigation();
    const location = useLocation();
    const { sendRequest } = useRequest();

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sortBy, setSortBy] = useState('name_asc');
    const [alphabetFilter, setAlphabetFilter] = useState('#');

    const observer = useRef();
    const isLoadingMore = useRef(false);

    const sortOptions = [
        { value: 'name_asc', label: t('component.search.sort.name_asc') },
        { value: 'name_desc', label: t('component.search.sort.name_desc') },
        { value: 'price_asc', label: t('component.search.sort.price_asc') },
        { value: 'price_desc', label: t('component.search.sort.price_desc') },
        { value: 'pzn', label: 'PZN' },
    ];

    const lastProductElementRef = useCallback(node => {
        if (loading || isLoadingMore.current) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMore.current) {
                isLoadingMore.current = true;
                setPage(prevPage => prevPage + 1);
            }
        }, {
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchProducts = async (pageNum = 1, reset = false) => {
        try {
            setLoading(true);
            isLoadingMore.current = true;

            const response = await sendRequest({
                method: 'POST',
                url: '/module/product/searchProducts',
                data: {
                    query: alphabetFilter === '#' ? null : alphabetFilter,
                    page: pageNum,
                    limit: 20,
                    sortBy: sortBy,
                    sortOrder: 'ASC',
                    allProducts: alphabetFilter === '#'
                }
            });

            if (response?.status === 'success' && response?.data) {
                let filteredProducts = response.data;

                if (reset || pageNum === 1) {
                    setProducts(filteredProducts);
                } else {
                    setProducts(prev => [...prev, ...filteredProducts]);
                }

                setHasMore(filteredProducts.length === 20);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error(t('component.search.general.error'));
        } finally {
            setLoading(false);
            isLoadingMore.current = false;
        }
    };

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        fetchProducts(1, true);
    }, [sortBy, alphabetFilter]);

    useEffect(() => {
        if (page > 1) {
            fetchProducts(page);
        }
    }, [page]);

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
    };

    return (
        <BringzzPageContainer
            className="bg-sand pb-32"
            authenticated={false}
            footerHover={true}
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={backPress}
                />,
                title: t('component.search.options.products')
            }}
        >
            <div className='pt-2'>
                <div className='rounded-xl bg-white mx-2 py-2'>
                    {/* Alphabet Filter */}
                    <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2 border-b' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                        <BringzzText tag='h3' className='whitespace-nowrap mr-1'>A-Z</BringzzText>
                        <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                            {alphabets.map((char, index) => {
                                const isSelected = char === alphabetFilter;
                                return (
                                    <div
                                        key={index}
                                        className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                            isSelected && "!bg-magic-lilac"
                                        )}
                                        onClick={() => setAlphabetFilter(char)}
                                    >
                                        <BringzzText tag='h5'>{char}</BringzzText>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="p-4 border-b">
                        <BringzzDropdown
                            options={sortOptions}
                            value={sortBy}
                            onChange={handleSortChange}
                            placeholder={t('component.search.general.sort_by')}
                        />
                    </div>

                    {/* Products List */}
                    {products.length > 0 ? (
                        products.map((product, index) => (
                            <div
                                key={product.id || product.pzn || index}
                                className="p-4 border-b flex items-center justify-between cursor-pointer"
                                ref={index === products.length - 1 ? lastProductElementRef : null}
                                onClick={() => navigate(`/product/details/${product.integrationId || product.pzn}`)}
                            >
                                <BringzzText tag='h4' className="flex-grow">
                                    {product.name || `PZN: ${product.pzn}`} • {product.companyName || ''}
                                </BringzzText>
                                <BringzzIcon
                                    size="20"
                                    icon="IconChevronRight"
                                    folder="LineIcons"
                                    className="text-gray-400 flex-shrink-0 ml-2"
                                />
                            </div>
                        ))
                    ) : loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <div className="p-4 text-center">
                            <BringzzText tag='p'>{t('component.search.empty.no_products')}</BringzzText>
                        </div>
                    )}

                    {loading && products.length > 0 && (
                        <div className="flex items-center justify-center p-4">
                            <BringzzIcon
                                size="24"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    )}
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default Products;